import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Crown, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Membership {
  id: string;
  plan_code: string;
  status: string;
  started_at: string;
}

export default function Membership() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [membership, setMembership] = useState<Membership | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    if (!user) return;

    async function fetchMembership() {
      const { data, error } = await supabase
        .from("memberships")
        .select("*")
        .eq("user_id", user!.id)
        .eq("status", "active")
        .single();

      if (error && error.code !== "PGRST116") {
        console.error("Error fetching membership:", error);
      } else {
        setMembership(data);
      }
      setLoading(false);
    }

    fetchMembership();
  }, [user]);

  async function handleJoin() {
    if (!user) return;
    setJoining(true);

    try {
      const { data, error } = await supabase.rpc("start_free_membership", {
        p_user_id: user.id,
      });

      if (error) throw error;

      // Fetch updated membership
      const { data: updated } = await supabase
        .from("memberships")
        .select("*")
        .eq("id", data)
        .single();

      setMembership(updated);
      toast({
        title: "Welcome to Flexaura Membership!",
        description: "You're now enrolled in our free membership program.",
      });
    } catch (error) {
      console.error("Error joining membership:", error);
      toast({
        title: "Error",
        description: "Unable to join membership. Please try again.",
        variant: "destructive",
      });
    } finally {
      setJoining(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const benefits = [
    "Bonus points accelerator (+0.5% on all bookings)",
    "Priority customer support",
    "Early access to exclusive offers",
    "Birthday bonus points",
    "No annual fee",
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Membership</h1>
        <p className="text-muted-foreground">Manage your Flexaura membership benefits</p>
      </div>

      {membership ? (
        <Card className="border-primary/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-primary" />
                <CardTitle>Flexaura Member</CardTitle>
              </div>
              <Badge>{membership.status}</Badge>
            </div>
            <CardDescription>
              Member since {new Date(membership.started_at).toLocaleDateString()}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-medium mb-2">Your Benefits</h3>
              <ul className="space-y-2">
                {benefits.map((benefit, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-primary" />
              <CardTitle>Join Flexaura Membership</CardTitle>
            </div>
            <CardDescription>
              Get exclusive benefits and earn points faster — completely free!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-medium mb-2">What You'll Get</h3>
              <ul className="space-y-2">
                {benefits.map((benefit, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Button onClick={handleJoin} disabled={joining} className="w-full">
              {joining ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Joining...
                </>
              ) : (
                "Join Free Membership"
              )}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
